import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Inbox, BarChart3, ScrollText, Save, RefreshCw, KeyRound, Send, Radar } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { initAnalytics } from "@/lib/analytics";
import { OutreachTab } from "@/components/admin/OutreachTab";
import { DossierTab } from "@/components/admin/DossierTab";

const TABS = [
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "dossier", label: "Dossier", icon: Radar },
  { id: "outreach", label: "Outreach", icon: Send },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "legal", label: "Legal info", icon: ScrollText },
  { id: "account", label: "Account", icon: KeyRound },
];

const ANALYTICS_FIELDS = [
  { key: "plausible_domain", label: "Plausible domain", hint: "e.g. chateaux.ai" },
  { key: "meta_pixel_id", label: "Meta Pixel ID", hint: "numeric ID from Meta Events Manager" },
  { key: "google_ads_id", label: "Google Ads ID", hint: "e.g. AW-123456789" },
  { key: "google_ads_label", label: "Google Ads conversion label", hint: "the label after the slash" },
];

const LEGAL_FIELDS = [
  { key: "OWNER_NAME", label: "Legal owner name", hint: "sole proprietor / entity name" },
  { key: "BUSINESS_ADDRESS", label: "Business address", hint: "registered mailing address" },
  { key: "JURISDICTION", label: "Governing jurisdiction", hint: "e.g. Delaware, United States" },
  { key: "LAUNCH_DATE", label: "Effective / launch date", hint: "e.g. June 15, 2026" },
  { key: "DPO_EMAIL", label: "Privacy / DPO email", hint: "e.g. privacy@chateaux.ai" },
];

const Field = ({ label, hint, value, onChange, testid }) => (
  <div className="mb-6">
    <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none"
    />
    {hint && <p className="mt-1.5 text-xs text-ink/45">{hint}</p>}
  </div>
);

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initialTab = pathname.startsWith("/admin/dossier") ? "dossier" : "leads";
  const [tab, setTab] = useState(initialTab);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [legal, setLegal] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const loadLeads = useCallback(() => {
    setLoadingLeads(true);
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoadingLeads(false));
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setAnalytics(r.data.analytics || {});
      setLegal(r.data.legal || {});
    });
    loadLeads();
  }, [loadLeads]);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { analytics, legal });
      setAnalytics(data.analytics);
      setLegal(data.legal);
      initAnalytics(data.analytics); // apply immediately in this tab
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pwd.new_password !== pwd.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated. Use it next time you sign in.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password.");
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-ink/10 bg-bone/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl text-ink">Chateaux</span>
            <span className="text-xs uppercase tracking-[0.2em] text-coral">Content Studio</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-ink/50">{admin?.email}</span>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-coral transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-10">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-ink/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-coral text-ink" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Leads */}
        {tab === "leads" && (
          <div data-testid="admin-leads">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-3xl text-ink">Trial leads</h2>
                <p className="text-sm text-ink/55">{leads.length} total signups</p>
              </div>
              <button onClick={loadLeads} className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-coral">
                <RefreshCw className={`w-4 h-4 ${loadingLeads ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
            <div className="border border-ink/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ink/[0.03] text-left text-xs uppercase tracking-[0.1em] text-ink/50">
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Source</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && !loadingLeads && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-ink/40">No leads yet.</td></tr>
                  )}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-ink/10" data-testid="admin-lead-row">
                      <td className="px-4 py-3 text-ink">{l.email}</td>
                      <td className="px-4 py-3 text-ink/70">{l.name || "—"}</td>
                      <td className="px-4 py-3 text-ink/70">{l.role || "—"}</td>
                      <td className="px-4 py-3 text-ink/70">{l.plan || "—"}</td>
                      <td className="px-4 py-3 text-ink/50">{l.source || "—"}</td>
                      <td className="px-4 py-3 text-ink/50">
                        {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Outreach */}
        {tab === "outreach" && <OutreachTab />}

        {tab === "dossier" && <DossierTab />}

        {/* Analytics */}
        {tab === "analytics" && (
          <div className="max-w-xl" data-testid="admin-analytics">
            <h2 className="font-serif text-3xl text-ink mb-1">Analytics & tags</h2>
            <p className="text-sm text-ink/55 mb-8">
              Leave a field blank to disable that tag. Changes apply on the next page load for visitors.
            </p>
            {ANALYTICS_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                hint={f.hint}
                value={analytics[f.key]}
                onChange={(v) => setAnalytics((a) => ({ ...a, [f.key]: v }))}
                testid={`analytics-${f.key}`}
              />
            ))}
            <SaveButton onClick={saveSettings} saving={saving} />
          </div>
        )}

        {/* Legal */}
        {tab === "legal" && (
          <div className="max-w-xl" data-testid="admin-legal">
            <h2 className="font-serif text-3xl text-ink mb-1">Legal information</h2>
            <p className="text-sm text-ink/55 mb-8">
              These values fill the {"{{placeholders}}"} in your Privacy, Terms, Cookie, Data-Deletion
              and Acceptable-Use pages automatically.
            </p>
            {LEGAL_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                hint={f.hint}
                value={legal[f.key]}
                onChange={(v) => setLegal((l) => ({ ...l, [f.key]: v }))}
                testid={`legal-${f.key}`}
              />
            ))}
            <SaveButton onClick={saveSettings} saving={saving} />
          </div>
        )}

        {/* Account */}
        {tab === "account" && (
          <div className="max-w-md" data-testid="admin-account">
            <h2 className="font-serif text-3xl text-ink mb-1">Change password</h2>
            <p className="text-sm text-ink/55 mb-8">
              Signed in as {admin?.email}. Choose a new password (min 8 characters).
            </p>
            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Current password</label>
              <input
                type="password"
                value={pwd.current_password}
                onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
                data-testid="pwd-current"
                autoComplete="current-password"
                className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none"
              />
            </div>
            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">New password</label>
              <input
                type="password"
                value={pwd.new_password}
                onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                data-testid="pwd-new"
                autoComplete="new-password"
                className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none"
              />
            </div>
            <div className="mb-8">
              <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Confirm new password</label>
              <input
                type="password"
                value={pwd.confirm}
                onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                data-testid="pwd-confirm"
                autoComplete="new-password"
                className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none"
              />
            </div>
            <button
              onClick={changePassword}
              disabled={pwdSaving}
              data-testid="pwd-submit"
              className="inline-flex items-center gap-2 bg-ink text-bone px-7 py-3.5 text-sm font-medium hover:bg-coral transition-colors disabled:opacity-60"
            >
              <KeyRound className="w-4 h-4" />
              {pwdSaving ? "Updating…" : "Update password"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const SaveButton = ({ onClick, saving }) => (
  <button
    onClick={onClick}
    disabled={saving}
    data-testid="admin-save"
    className="group inline-flex items-center gap-2 bg-ink text-bone px-7 py-3.5 text-sm font-medium hover:bg-coral transition-colors disabled:opacity-60"
  >
    <Save className="w-4 h-4" />
    {saving ? "Saving…" : "Save changes"}
  </button>
);
